import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel } from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
  ChartBarSquareIcon,
  CircleStackIcon,
  CloudIcon,
  CpuChipIcon,
  CubeTransparentIcon,
} from '@heroicons/react/24/outline';
import { SOLUTION_CATEGORIES, SEMANTIC_HR_NAV_CATEGORY } from '../../../content/enterpriseMessaging';
import { BRAND } from '../../../content/staticAssets';
import UpdatesBanner from './UpdatesBanner';
import './global.css';

const ICON_MAP = {
  CloudIcon,
  ChartBarSquareIcon,
  CircleStackIcon,
  CpuChipIcon,
  CubeTransparentIcon,
};

const navigation = [
  {
    name: 'Solutions',
    type: 'dropdown',
    categories: SOLUTION_CATEGORIES,
  },
  { name: 'Pricing', type: 'button', href: '/pricing' },
  { name: 'Delivery', type: 'button', href: '/delivery' },
  { name: 'Company', type: 'button', href: '/company' },
  {
    name: 'Semantic HR',
    type: 'dropdown',
    highlight: true,
    // Last trigger in the row: anchor the panel right so it stays on screen.
    panelAlign: 'right',
    categories: [SEMANTIC_HR_NAV_CATEGORY],
  },
  { name: 'Get a Demo', href: '/contact', type: 'link' },
];

/** Strip a trailing/inline ™ so we never render “Semantic HR™ ™”. */
function withoutTrademark(name) {
  return String(name || '')
    .replace(/\u2122/g, '')
    .replace(/™/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function TrademarkLabel({ name, mark = false, supClassName = 'ml-0.5 text-[9px]' }) {
  return (
    <>
      {withoutTrademark(name)}
      {mark ? <sup className={supClassName}>™</sup> : null}
    </>
  );
}

function SolutionDropdownItems({ categories, onNavigate, compact = false }) {
  return (
    <div
      className={
        compact
          ? 'py-1'
          : 'max-h-[min(72vh,38rem)] overflow-y-auto overscroll-contain py-1'
      }
    >
      {categories.map((cat) => {
        const Icon = ICON_MAP[cat.icon] || CloudIcon;
        const iconBoxClass = cat.iconClass || 'bg-brand-50 text-brand-600';
        return (
          <div key={cat.id} className="border-b border-ink-100 last:border-b-0">
            <a
              href={cat.href}
              onClick={onNavigate}
              className="flex items-start gap-x-2.5 px-3.5 py-2 hover:bg-surface-muted transition-colors"
            >
              <span
                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md ${iconBoxClass}`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
              </span>
              <div className="min-w-0 max-w-full">
                <span className="block text-[14px] font-semibold leading-tight text-ink-900">
                  <TrademarkLabel name={cat.name} mark={Boolean(cat.featured)} />
                </span>
                <p className="nav-menu-desc mt-0.5 break-words text-[12px] leading-snug text-ink-500">
                  {cat.shortDescription || cat.description}
                </p>
              </div>
            </a>
            {cat.offerings?.length > 0 && (
              <div className="space-y-0 pb-1.5 pl-12 pr-3.5">
                {cat.offerings.map((offering) => (
                  <a
                    key={offering.href + offering.name}
                    href={offering.href}
                    onClick={onNavigate}
                    className="block rounded-md px-2 py-1 text-[12px] font-medium text-ink-600 hover:bg-brand-50/50 hover:text-brand-600 transition-colors"
                  >
                    <TrademarkLabel name={offering.name} mark={Boolean(offering.featured)} />
                  </a>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function dropdownTriggerClass(open, highlight) {
  if (highlight) {
    return open
      ? 'text-logo-light-blue bg-accent-50/80'
      : 'text-logo-light-blue hover:text-accent-500 hover:bg-accent-50/70';
  }
  return open
    ? 'text-brand-600 bg-brand-50/70'
    : 'text-ink-800 hover:text-brand-600 hover:bg-surface-muted/70';
}

function menuId(name) {
  return `nav-menu-${String(name).replace(/\s+/g, '-').toLowerCase()}`;
}

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  const closeMobile = () => setMobileMenuOpen(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const solid = scrolled || openDropdown !== null;

  return (
    <>
      <UpdatesBanner />
      <header
        className={`sticky top-0 z-50 transition-[background-color,box-shadow,backdrop-filter,border-color] duration-300 ${
          solid
            ? 'bg-white/85 supports-[backdrop-filter]:bg-white/70 backdrop-blur-md border-b border-ink-200/70 shadow-sm'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
      <nav
        aria-label="Global"
        className={`mx-auto flex w-full max-w-[130rem] items-center justify-between px-4 transition-[height] duration-300 sm:px-6 lg:px-12 ${
          scrolled ? 'h-20 lg:h-24' : 'h-20 sm:h-24 lg:h-28'
        }`}
      >
        <div className="flex min-w-0 items-center lg:flex-1">
          <a href="/" className="flex items-center -m-1.5 overflow-hidden p-1.5">
            <img
              alt="MyOrgLab Logo"
              data-brand-logo
              src={BRAND.logoTransparent}
              className={`w-auto max-w-full transition-[height] duration-300 ${scrolled ? 'h-16 sm:h-20' : 'h-16 sm:h-20 lg:h-24'}`}
            />
          </a>
        </div>

        <div className="hidden lg:flex lg:items-center lg:gap-x-1.5">
          {navigation.filter((item) => item.type !== 'link').map((item) =>
            item.type === 'dropdown' ? (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => setOpenDropdown(item.name)}
                onMouseLeave={() => setOpenDropdown(null)}
                onKeyDown={(event) => { if (event.key === 'Escape') setOpenDropdown(null); }}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) setOpenDropdown(null);
                }}
              >
                {/* Pointer users get hover; click opens the panel for keyboard and touch. */}
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={openDropdown === item.name}
                  aria-controls={menuId(item.name)}
                  onClick={() => setOpenDropdown((open) => (open === item.name ? null : item.name))}
                  className={`flex items-center gap-1 rounded-lg px-3.5 py-2.5 text-[15px] font-semibold tracking-tight transition-colors ${dropdownTriggerClass(openDropdown === item.name, item.highlight)}`}
                >
                  <TrademarkLabel name={item.name} mark={Boolean(item.highlight)} supClassName="ml-0.5 text-[11px]" />
                  <svg
                    className={`h-4 w-4 transition-transform ${openDropdown === item.name ? 'rotate-180' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  </svg>
                </button>
                {openDropdown === item.name && (
                  // Width must live on the absolutely positioned panel — not a % of
                  // the label-sized trigger. pt-2 is a hover bridge so mouseleave
                  // does not fire in the visual gap before the pointer reaches the menu.
                  <div
                    id={menuId(item.name)}
                    role="menu"
                    className={`absolute top-full z-50 min-w-[18rem] max-w-[32rem] pt-2 w-[min(32rem,calc(100vw-2rem))] ${
                      item.panelAlign === 'right' ? 'right-0 left-auto' : 'left-0'
                    }`}
                    style={{ width: 'min(32rem, calc(100vw - 2rem))' }}
                  >
                    <div className="overflow-hidden rounded-xl border border-ink-200 bg-white shadow-lg">
                      <SolutionDropdownItems
                        categories={item.categories}
                        compact={item.panelAlign === 'right'}
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <a
                key={item.name}
                href={item.href}
                className={
                  item.highlight
                    ? 'rounded-lg px-3.5 py-2.5 text-[15px] font-semibold tracking-tight text-logo-light-blue hover:bg-accent-50/70 hover:text-accent-500 transition-colors'
                    : 'rounded-lg px-3.5 py-2.5 text-[15px] font-semibold tracking-tight text-ink-800 hover:bg-surface-muted/70 hover:text-brand-600 transition-colors'
                }
              >
                {item.highlight ? (
                  <TrademarkLabel name={item.name} mark supClassName="ml-0.5 text-[11px]" />
                ) : (
                  item.name
                )}
              </a>
            ),
          )}
        </div>

        <div className="flex lg:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="-m-2.5 inline-flex min-h-11 min-w-11 items-center justify-center rounded-md p-2.5 text-ink-700"
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon aria-hidden="true" className="h-8 w-auto" />
          </button>
        </div>

        <div className="hidden lg:flex lg:flex-1 lg:items-center lg:justify-end lg:gap-x-2">
          <a
            href="https://auth.myorglab.com/login?client_id=6dk02q50qned44flliaqhpckvf&response_type=code&scope=email+openid+profile&redirect_uri=https://www.mediahub.myorglab.com/callback"
            className="btn-ghost text-sm font-medium"
          >
            Sign In
          </a>
          <a href="/contact" className="btn-primary">
            Let&apos;s Talk
          </a>
        </div>
      </nav>

      <Dialog open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} className="lg:hidden">
        <div className="fixed inset-0 z-40 bg-ink-900/20" />
        {/* Extra bottom padding keeps the last links clear of the fixed cookie bar. */}
        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto border-l border-ink-200 bg-white px-6 py-6 pb-36 sm:max-w-sm sm:pb-32">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center -m-1.5 p-1.5">
              <img
                alt="MOL Logo"
                src={BRAND.favicon}
                className="h-12 w-auto"
              />
            </a>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 inline-flex min-h-11 min-w-11 items-center justify-center rounded-md p-2.5 text-ink-600 hover:bg-surface-muted"
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="h-6 w-6" />
            </button>
          </div>

          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-ink-200">
              <div className="space-y-1 py-6">
                {navigation.filter((item) => item.type !== 'link').map((item) =>
                  item.type === 'dropdown' ? (
                    <div key={item.name}>
                      <button
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={mobileDropdownOpen === item.name}
                        onClick={() =>
                          setMobileDropdownOpen(mobileDropdownOpen === item.name ? null : item.name)
                        }
                        className={`-mx-3 flex min-h-11 w-full items-center justify-between rounded-lg px-3 py-3 text-left text-[17px] font-semibold tracking-tight ${
                          item.highlight
                            ? 'text-logo-light-blue hover:bg-accent-50'
                            : 'text-ink-900 hover:bg-surface-muted'
                        }`}
                      >
                        <span>
                          <TrademarkLabel name={item.name} mark={Boolean(item.highlight)} supClassName="ml-0.5 text-[11px]" />
                        </span>
                        <svg
                          className={`h-5 w-5 text-ink-400 transition-transform ${mobileDropdownOpen === item.name ? 'rotate-180' : ''}`}
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                        </svg>
                      </button>
                      {mobileDropdownOpen === item.name && (
                        <div className="ml-1 mt-1 overflow-hidden rounded-lg border border-ink-100">
                          <SolutionDropdownItems categories={item.categories} onNavigate={closeMobile} compact />
                        </div>
                      )}
                    </div>
                  ) : (
                    <a
                      key={item.name}
                      href={item.href}
                      className={
                        item.highlight
                          ? '-mx-3 block min-h-11 rounded-lg px-3 py-3 text-[17px] font-semibold tracking-tight text-logo-light-blue hover:bg-accent-50'
                          : '-mx-3 block min-h-11 rounded-lg px-3 py-3 text-[17px] font-semibold tracking-tight text-ink-900 hover:bg-surface-muted'
                      }
                    >
                      {item.highlight ? (
                        <TrademarkLabel name={item.name} mark supClassName="ml-0.5 text-[11px]" />
                      ) : (
                        item.name
                      )}
                    </a>
                  ),
                )}
              </div>
              <div className="space-y-2 py-6">
                <a
                  href="https://auth.myorglab.com/login?client_id=6dk02q50qned44flliaqhpckvf&response_type=code&scope=email+openid+profile&redirect_uri=https://www.mediahub.myorglab.com/callback"
                  className="btn-ghost w-full text-center"
                >
                  Sign In
                </a>
                <a href="/contact" className="btn-primary w-full">
                  Let&apos;s Talk
                </a>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
      </header>
    </>
  );
}
